import type { DataModelState, DataTypeDto, Site } from '@bk/contracts';
import { useState, type ReactNode } from 'react';
import { APP_NAME } from '../../lib/app-name';
import { useLogoOnDark } from '../../lib/brand';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { AppMark } from '../../ui';

/** Una voce del menu: `path` dopo `#/t/<azienda>/`. */
interface Item {
  path: string;
  label: string;
  count?: number;
  /** Quante cose aspettano una scelta (es. le sedi proposte, ADR 0141): badge in evidenza. */
  alert?: number;
}

/** Un gruppo del menu: si apre e si chiude; `items` vuoto = voce singola che porta a `path`. */
interface Group {
  key: string;
  label: string;
  icon: ReactNode;
  items: Item[];
  path?: string;
}

/** Testo confrontabile: minuscolo e senza accenti. */
const fold = (v: string) =>
  v
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

const byLabel = (a: DataTypeDto, b: DataTypeDto) => a.etichetta.localeCompare(b.etichetta, 'it', { sensitivity: 'base' });

/** Icona a tratto, 18px, nel colore del testo. */
function Icon({ d }: { d: string }) {
  return (
    <svg viewBox="0 0 24 24" className="h-[18px] w-[18px] shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d={d} />
    </svg>
  );
}

const ICONS = {
  oggi: 'M3 10.5 12 4l9 6.5V20a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z',
  azienda: 'M4 21V5a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v16M15 9h4a1 1 0 0 1 1 1v11M3 21h18M8 8h3M8 12h3M8 16h3',
  soggetti: 'M16 20v-1.5A3.5 3.5 0 0 0 12.5 15h-5A3.5 3.5 0 0 0 4 18.5V20M10 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7M20 20v-1.5a3.5 3.5 0 0 0-2.5-3.35M15.5 4.15a3.5 3.5 0 0 1 0 6.7',
  cose: 'M21 8 12 3 3 8v8l9 5 9-5zM3 8l9 5 9-5M12 13v8',
  lavoro: 'M9 6V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v1M4 6h16a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1M3 12h18',
  fonti: 'M12 3c4.4 0 8 1.3 8 3s-3.6 3-8 3-8-1.3-8-3 3.6-3 8-3M4 6v6c0 1.7 3.6 3 8 3s8-1.3 8-3V6M4 12v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6',
};

/**
 * Menu laterale dell'azienda (ADR 0112): le entità vengono dal modello dei dati, quindi il menu è diverso per ogni
 * azienda e cambia da solo quando il modello cambia. `active` è il percorso della pagina aperta.
 */
export function Sidebar({ active, logo }: { active: string; logo: string | null }) {
  const { tenant, isManager, procedureAreas } = useTenant();
  const dark = useLogoOnDark(logo);
  const model = useTenantQuery<DataModelState>(['data-model'], '/data-model');
  const sites = useTenantQuery<{ data: Site[] }>(['sites'], '/sites');
  const proposals = (sites.data?.data ?? []).filter((x) => x.status === 'da_confermare').length;
  const [search, setSearch] = useState('');
  const types = (model.data?.data ?? []).filter((x) => x.stato === 'attivo').sort(byLabel);
  const entity = (x: DataTypeDto): Item => ({ path: `e/${x.chiave}`, label: x.etichetta, count: x.entita.length });
  const groups: Group[] = [
    { key: 'oggi', label: 'Oggi', icon: <Icon d={ICONS.oggi} />, items: [], path: 'oggi' },
    {
      key: 'azienda',
      label: 'L’azienda',
      icon: <Icon d={ICONS.azienda} />,
      items: [
        { path: 'impresa', label: 'Impresa' },
        { path: 'organi', label: 'Organi e cariche' },
        // I conti dal bilancio: solo per il titolare e i responsabili (ADR 0132).
        ...(isManager ? [{ path: 'conti', label: 'Conti' }] : []),
        { path: 'sedi', label: 'Sedi', alert: isManager ? proposals : 0 },
        { path: 'persone', label: 'Persone' },
      ],
    },
    { key: 'soggetti', label: 'Soggetti', icon: <Icon d={ICONS.soggetti} />, items: types.filter((x) => x.soggetto_esterno).map(entity) },
    { key: 'cose', label: 'Cose gestite', icon: <Icon d={ICONS.cose} />, items: types.filter((x) => !x.soggetto_esterno).map(entity) },
    {
      key: 'lavoro',
      label: 'Lavoro',
      icon: <Icon d={ICONS.lavoro} />,
      items: [
        ...(isManager || procedureAreas.length > 0 ? [{ path: 'procedure', label: 'Procedure' }] : []),
        { path: 'processi', label: 'Processi' },
        { path: 'esecuzioni', label: 'Esecuzioni' },
        { path: 'controlli', label: 'Controlli' },
        { path: 'indicatori', label: 'Indicatori' },
        ...(isManager ? [{ path: 'proposte', label: 'Proposte' }] : []),
      ],
    },
    {
      key: 'fonti',
      label: 'Fonti',
      icon: <Icon d={ICONS.fonti} />,
      items: [
        ...(isManager ? [{ path: 'raccolta', label: 'Raccolta dei dati' }] : []),
        { path: 'archivio', label: 'Archivio' },
        { path: 'software', label: 'Software' },
        { path: 'dati/salvati', label: 'Tutti i dati salvati' },
      ],
    },
  ];
  // Aperti all'inizio: il gruppo della pagina aperta e quelli delle entità, che sono il cuore del menu.
  const [open, setOpen] = useState<Set<string>>(
    () => new Set(['soggetti', 'cose', ...groups.filter((g) => g.items.some((i) => i.path === active)).map((g) => g.key)]),
  );
  const toggle = (key: string) =>
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  const q = fold(search.trim());
  const shown = groups
    .map((g) => ({ ...g, items: q && !fold(g.label).includes(q) ? g.items.filter((i) => fold(i.label).includes(q)) : g.items }))
    .filter((g) => (g.path ? !q || fold(g.label).includes(q) : g.items.length > 0));
  const href = (path: string) => `#/t/${tenant.id}/${path}`;
  const row = 'flex w-full items-center gap-3 rounded-[10px] px-3 py-2.5 text-left text-[13px] font-medium tracking-[0.01em] transition-colors';
  return (
    <nav className="flex h-full flex-col">
      {/* Il logo dell'azienda quando c'è (ADR 0117), altrimenti il marchio del prodotto. */}
      <a href={`#/t/${tenant.id}/oggi`} className="flex min-h-[4.5rem] items-center gap-2.5 px-5 pb-4 pt-5" title={tenant.name}>
        {logo ? (
          <img src={logo} alt={tenant.name} className={`max-h-10 max-w-full object-contain ${dark ? 'rounded-lg bg-ink px-2 py-1.5' : ''}`} />
        ) : (
          <>
            <span className="grid h-8 w-8 place-items-center rounded-[9px] bg-accent text-white"><AppMark className="h-full w-full" /></span>
            <span className="text-lg font-semibold tracking-tight">{APP_NAME}</span>
          </>
        )}
      </a>
      <div className="px-3 pb-2">
        <input
          className="w-full rounded-[10px] border border-black/[0.06] bg-paper px-3 py-2 text-sm outline-none placeholder:text-ink/40 focus:border-accent/40 focus:bg-white"
          placeholder="Cerca nel menu"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <div className="min-h-0 flex-1 space-y-0.5 overflow-y-auto px-3 pb-4">
        {shown.map((g) => {
          if (g.path) {
            const current = active === g.path;
            return (
              <a key={g.key} href={href(g.path)} className={`${row} ${current ? 'bg-accent/10 text-accent' : 'text-ink/80 hover:bg-accent/[0.06] hover:text-ink'}`}>
                <span className="text-accent">{g.icon}</span>
                {g.label}
              </a>
            );
          }
          const expanded = !!q || open.has(g.key);
          const hasActive = g.items.some((i) => i.path === active);
          return (
            <div key={g.key}>
              <button className={`${row} ${hasActive && !expanded ? 'bg-accent/10 text-accent' : 'text-ink/80 hover:bg-accent/[0.06] hover:text-ink'}`} aria-expanded={expanded} onClick={() => toggle(g.key)}>
                <span className="text-accent">{g.icon}</span>
                <span className="min-w-0 flex-1 truncate">{g.label}</span>
                {!expanded && g.items.some((i) => i.alert) && <span aria-label="Proposte da scegliere" className="h-2 w-2 rounded-full bg-amber-500" />}
                <svg viewBox="0 0 24 24" className={`h-4 w-4 text-ink/35 transition-transform duration-300 ${expanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <path d="m9 6 6 6-6 6" />
                </svg>
              </button>
              {expanded && (
                <ul className="mb-1 ml-[22px] mt-0.5 border-l border-accent/15 pl-2">
                  {g.items.map((i) => {
                    const current = active === i.path;
                    return (
                      <li key={i.path}>
                        <a
                          href={href(i.path)}
                          className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-[13px] transition-colors ${current ? 'bg-accent/10 font-medium text-accent' : i.count === 0 ? 'text-ink/40 hover:bg-accent/[0.06]' : 'text-ink/70 hover:bg-accent/[0.06] hover:text-ink'}`}
                        >
                          <span className="min-w-0 flex-1 truncate">{i.label}</span>
                          {!!i.alert && (
                            <span title={i.alert === 1 ? '1 proposta da scegliere' : `${i.alert} proposte da scegliere`} className="rounded-md bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold tabular-nums leading-none text-white">
                              {i.alert.toLocaleString('it-IT')}
                            </span>
                          )}
                          {!!i.count && (
                            <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-bold tabular-nums leading-none ${current ? 'bg-accent text-white' : 'bg-accent/10 text-accent'}`}>
                              {i.count.toLocaleString('it-IT')}
                            </span>
                          )}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
        {!shown.length && <p className="px-3 py-2 text-xs text-ink/50">Nessuna voce contiene «{search.trim()}».</p>}
        {model.data && !types.length && !q && (
          <p className="px-3 py-2 text-xs text-ink/50">Le entità dell’azienda compaiono qui quando il sistema ha ricavato il modello dei dati.</p>
        )}
      </div>
    </nav>
  );
}
